import { ArrowUpRight, ArrowDownLeft, Copy, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { copyToClipboard } from '@/utils/clipboard'

type LatestTransactionProps = {
  title: string
  address: string
  fullAddress?: string
  amount: string
  coin: string
  time: string
  type?: 'send' | 'receive'
  onClick?: () => void
}

const LatestTransaction = ({
  title,
  address,
  fullAddress,
  amount,
  coin,
  time,
  type = 'receive',
  onClick
}: LatestTransactionProps) => {
  const { t } = useTranslation()
  const isSend = type === 'send' || title.toLowerCase().includes('sent')
  const [copied, setCopied] = useState(false)

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const toCopy = fullAddress || address
    if (!toCopy) return
    const success = await copyToClipboard(toCopy)
    if (success) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div
      onClick={onClick}
      className={`flex flex-row justify-between items-center py-2 ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
    >
      <div className="flex flex-row gap-4 items-center">
        <div
          className={`flex items-center justify-center rounded-full h-12 w-12 bg-text-main/5`}
        >
          {isSend ? (
            <ArrowUpRight className="text-accent w-5 h-5" />
          ) : (
            <ArrowDownLeft className="text-success w-5 h-5" />
          )}
        </div>
        <div className="flex flex-col gap-0.75">
          <p className="text-text-main font-semibold">{title}</p>
          <div className="flex items-center gap-1.5">
            <p className="text-text-secondary text-xs truncate max-w-30 sm:hidden">
              {isSend
                ? `${t('dashboard.tx_to')}${address}`
                : `${t('dashboard.tx_from')}${address}`}
            </p>
            <p className="text-text-secondary text-xs truncate hidden sm:block">
              {isSend
                ? `${t('dashboard.tx_to')}${fullAddress || address}`
                : `${t('dashboard.tx_from')}${fullAddress || address}`}
            </p>
            {(fullAddress || address) && (
              <button
                id={`copy-addr-${fullAddress || address}`}
                onClick={handleCopy}
                title={t('common.copy_address')}
                className="shrink-0 p-0.5 rounded text-text-secondary hover:text-primary transition-colors"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-success" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-0.75 items-end">
        <p className={`font-bold ${isSend ? 'text-accent' : 'text-success'}`}>
          {amount} {coin}
        </p>
        <p className="text-text-secondary text-xs">{time}</p>
      </div>
    </div>
  )
}

export default LatestTransaction
